"use client";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Dot,
} from "recharts";
import { practicalPassRateByRound } from "@/lib/pass-rate-data";

/**
 * 좁은 좌측 사이드바용 실기 합격률 추이 미니 차트 (JCG-2101).
 *
 * jeongcheogi-pass-rate 본문의 `practicalPassRateByRound` 라인 차트 축소판. 사이드바 폭(약
 * 320~372px)에 맞춰 축·그리드·인-차트 라벨은 생략하고, 본문 InteractiveChart 와 동일한 점 색상
 * 의미(최고=빨강·최저=파랑·최신=초록)만 공유한다. 핵심 수치는 하단 칩으로 요약한다.
 *
 * 라인은 브랜드 앰버(라이트/다크 공통), 칩·툴팁은 디자인 토큰으로 다크모드 자동 대응.
 */
const KEY = "실기 합격률";
const data = practicalPassRateByRound;
const values = data.map((d) => d[KEY] as number);
const maxValue = Math.max(...values);
const minValue = Math.min(...values);
const lastIdx = data.length - 1;
const lastPoint = data[lastIdx];

interface DotProps {
  cx?: number;
  cy?: number;
  value?: number;
  index?: number;
}

interface MiniTooltipProps {
  active?: boolean;
  payload?: Array<{ value?: number }>;
  label?: string | number;
}

function renderDot({ cx, cy, value, index }: DotProps) {
  const key = `mini-dot-${index}`;
  if (value === maxValue) {
    return <Dot key={key} cx={cx} cy={cy} r={4} fill="#ef4444" stroke="#fff" strokeWidth={1} />;
  }
  if (value === minValue) {
    return <Dot key={key} cx={cx} cy={cy} r={4} fill="#3b82f6" stroke="#fff" strokeWidth={1} />;
  }
  if (index === lastIdx) {
    return <Dot key={key} cx={cx} cy={cy} r={4} fill="#16a34a" stroke="#fff" strokeWidth={1} />;
  }
  return <Dot key={key} cx={cx} cy={cy} r={1.5} fill="#f59e0b" />;
}

function MiniTooltip({ active, payload, label }: MiniTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-[11px] shadow-md">
      <p className="font-bold text-foreground">{label}</p>
      <p className="text-muted-foreground">
        실기 합격률{" "}
        <span className="font-semibold text-amber-600 dark:text-amber-400">
          {payload[0].value}%
        </span>
      </p>
    </div>
  );
}

export default function PassRateMiniChart() {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="h-[140px] w-full"
        role="img"
        aria-label={`실기 합격률 추이. ${data[0].name}부터 ${lastPoint.name}까지 ${data.length}개 회차. 최고 ${maxValue}%, 최저 ${minValue}%, 최신(${lastPoint.name}) ${lastPoint[KEY]}%.`}
      >
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <XAxis dataKey="name" hide />
            <YAxis hide domain={[0, "dataMax + 5"]} />
            <Tooltip content={<MiniTooltip />} />
            <Line
              type="monotone"
              dataKey={KEY}
              stroke="#f59e0b"
              strokeWidth={2}
              dot={renderDot}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap items-center gap-1 text-[11px] font-semibold">
        <span className="rounded-md bg-red-50 px-1.5 py-0.5 text-red-600 dark:bg-red-950/40 dark:text-red-300">
          최고 {maxValue}%
        </span>
        <span className="rounded-md bg-blue-50 px-1.5 py-0.5 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300">
          최저 {minValue}%
        </span>
        <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300">
          최신 {lastPoint[KEY]}%
        </span>
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {data[0].name}~{lastPoint.name} 회차별 실기 합격률
      </p>
    </div>
  );
}
